import React, { useEffect, useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Box, 
  Typography, 
  Avatar,
  IconButton,
  CircularProgress,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import useTheme from '../hooks/useTheme';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 12,
    maxWidth: '90vw',
    maxHeight: '90vh',
    width: '100%',
    margin: 16,
  },
}));

const ChatContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflowY: 'auto',
  padding: theme.spacing(3),
  background: '#fff',
  maxHeight: '60vh',
  fontFamily: 'monospace',
  fontSize: '14px',
  lineHeight: '1.6',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  userSelect: 'text',
  cursor: 'text',
}));

const MessageLine = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  padding: theme.spacing(1, 0),
  borderBottom: `1px solid ${theme.borderColor || '#e0e0e0'}`,
  '&:last-child': {
    borderBottom: 'none',
  },
}));

const MessageHeader = styled(Box)(({ theme, isUser }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
  fontSize: '13px',
}));

const MessageHeaderText = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: '#000',
  fontSize: '13px',
}));

const MessageIcon = styled(Box)(({ theme, isUser }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  borderRadius: '50%',
  background: isUser ? '#e3f2fd' : '#f3e5f5',
  color: isUser ? '#1976d2' : '#7b1fa2',
  fontSize: '14px',
  lineHeight: '1',
  userSelect: 'text',
}));

const MessageContent = styled(Typography)(({ theme }) => ({
  color: theme.textColor || '#333',
  fontSize: '14px',
  lineHeight: '1.5',
}));

export default function ConversationModal({ open, onClose, userId }) {
  const theme = useTheme();
  const [user, setUser] = useState({ name: '', conversation: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    if (open && userId) {
      fetchConversation();
    }
  }, [open, userId]);

  const fetchConversation = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`https://api.uxhq.com/user_report?user_id=${userId}`);
      if (!res.ok) throw new Error('User not found');
      const data = await res.json();
      
      // Ensure conversation is an array
      let conversation = data.conversation;
      if (typeof conversation === 'string') {
        try { conversation = JSON.parse(conversation); } catch { conversation = []; }
      }
      if (!Array.isArray(conversation)) conversation = [];
      
      setUser({ name: data.name, conversation });
    } catch (err) {
      setError('Could not load conversation.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setUser({ name: '', conversation: [] });
    setError('');
    setCopySuccess(false);
    onClose();
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '00:00:00';
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('en-US', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      return '00:00:00';
    }
  };

  const getPlainTextConversation = () => {
    if (!Array.isArray(user.conversation) || user.conversation.length === 0) {
      return 'No conversation found.';
    }

    return user.conversation.map((msg, idx) => {
      const role = msg.role === 'user' ? 'User' : 'System';
      const time = formatTime(new Date().getTime() + (idx * 6000)); // Simulate timestamps
      const content = msg.content || '';
      return `${role} (${time}): ${content}`;
    }).join('\n\n');
  };

  const handleCopyChat = async () => {
    try {
      const chatText = getPlainTextConversation();
      
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(chatText);
      } else {
        // Fallback for older browsers or non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = chatText;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
      }
      
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy chat:', err);
      // Fallback to manual selection
      const chatContainer = document.querySelector('[data-chat-container]');
      if (chatContainer) {
        const range = document.createRange();
        range.selectNodeContents(chatContainer);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  };

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: `1px solid ${theme.borderColor}`,
        pb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 40, height: 40, background: '#e3e7ed', color: '#b0b7c3' }}>
            <PersonIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: theme.textColor }}>
              {user.name || 'Discovery Chat'}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.textSecondaryColor, fontSize: 12 }}>
              Conversation History (Plain Text)
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img 
              src="/Group 37387.png" 
              alt="UXHQ Logo" 
              style={{ 
                width: 80, 
                height: 80,
                objectFit: 'contain'
              }}
            />
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : (
          <ChatContainer data-chat-container>
            {Array.isArray(user.conversation) && user.conversation.length > 0 ? (
              user.conversation.map((msg, idx) => (
                <MessageLine key={idx}>
                  <MessageHeader isUser={msg.role === 'user'}>
                    <MessageHeaderText>
                      {msg.role === 'user' ? 'User' : 'System'} ({formatTime(new Date().getTime() + (idx * 6000))})
                    </MessageHeaderText>
                  </MessageHeader>
                  <MessageContent>
                    {msg.content || ''}
                  </MessageContent>
                </MessageLine>
              ))
            ) : (
              <Typography sx={{ color: theme.textSecondaryColor, textAlign: 'center', py: 4 }}>
                No conversation found.
              </Typography>
            )}
          </ChatContainer>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.borderColor}` }}>
        <Button onClick={handleClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </StyledDialog>
  );
} 